import { FileInfo } from '@/store/common.interface';
import { DTOLanguageEnabled, LanguageEnabled, LanguageTranslation, Locales } from '@/store/language.interface';
import type { DTOProject } from '@/store/project.interface';

export interface DTOPage extends DTOLanguageEnabled {
  id: number;
  slug: string | null;
  title: LanguageTranslation;
  status: number;
  theme_key: string | null;
  layout_key: string | null;
  language_enabled: Locales[];
  blocks: DTOPageBlock[];
}

export interface Page extends LanguageEnabled {
  id: number;
  slug: string | null;
  title: LanguageTranslation;
  // A draft reaches the site only through the admin's preview.
  status: number;
  themeKey: string | null;
  // Null for the theme's default layout.
  layoutKey: string | null;
  languageEnabled: Locales[];
  blocks: PageBlock[];
}

export interface DTOPageBlock {
  id: number;
  block_type: number;
  order_num: number;
  title: LanguageTranslation | null;
  config: PageBlockConfigDto;
}

export interface PageBlock {
  id: number;
  pageBlockType: number;
  orderNum: number;
  title: LanguageTranslation | null;
  config: PageBlockConfigDto;
}

export interface PageBlockHeroCTAButtonDto {
  enabled: boolean;
  text: LanguageTranslation;
  linkType: 'url' | 'module';
  url?: LanguageTranslation;
  moduleId?: number;
}

export interface PageBlockConfigDto {}

export interface PageBlockHeroConfigDto extends PageBlockConfigDto {
  ctaButtons: {
    button1: PageBlockHeroCTAButtonDto;
    button2: PageBlockHeroCTAButtonDto;
  };
  image: FileInfo | null;
  useCommunityBanner: boolean;
}

export interface PageBlockFeaturedProjectsProjectConfigDto {
  id: number;
  image: FileInfo | null;
  project?: DTOProject | null;
}

export interface PageBlockFeaturedProjectsConfigDto extends PageBlockConfigDto {
  backgroundGradientEndColor?: string | null;
  backgroundGradientStartColor?: string | null;
  backgroundIllustrationAsset?: string | null;
  backgroundIllustrationPosition?: string | null;
  hasOverlay: boolean;
  overlayColor?: string | null;
  overlayText?: LanguageTranslation;
  overlayTextColor?: string | null;
  description: LanguageTranslation;
  layout: number | null;
  projects: PageBlockFeaturedProjectsProjectConfigDto[] | null;
  title: LanguageTranslation;
  useCommunityFeaturedProject: boolean;
}

export interface PageBlockLinksCarouselItemDto {
  description: LanguageTranslation;
  href: LanguageTranslation;
  icon: FileInfo | null;
  mainImage: FileInfo | null;
  secondaryImage: FileInfo | null;
  title: LanguageTranslation;
}

export interface PageBlockLinksCarouselConfigDto extends PageBlockConfigDto {
  description: LanguageTranslation;
  items: PageBlockLinksCarouselItemDto[] | null;
  title: LanguageTranslation;
}

export function parsePage(dtoPage: DTOPage): Page {
  return {
    id: dtoPage.id,
    slug: dtoPage.slug ?? null,
    title: dtoPage.title,
    status: dtoPage.status,
    themeKey: dtoPage.theme_key ?? null,
    layoutKey: dtoPage.layout_key ?? null,
    languageEnabled: dtoPage.language_enabled,
    blocks: [...(dtoPage.blocks ?? [])]
      .sort((a, b) => a.order_num - b.order_num)
      .map((block) => ({
        id: block.id,
        pageBlockType: block.block_type,
        orderNum: block.order_num,
        title: block.title,
        config: block.config,
      })),
  };
}
